/**
 * TraceClient — Frontend trace span creation service.
 *
 * Creates threads and spans via the agent-service traces API.
 * Gated by trace level: if tracing is disabled (level 0 or init failed),
 * all methods are no-ops with zero cost.
 *
 * Usage:
 *   await traceClient.init();
 *   const threadId = await traceClient.createThread('data_source', 'Chart data fetch');
 *   const spanId = await traceClient.startSpan(threadId, 'ui_data_request', 'Fetching data');
 *   const headers = traceClient.getTraceHeaders(threadId, spanId);
 *   await traceClient.endSpan(spanId, 'ok', { detail: { rowCount: 42 } });
 */

import { tracesApi, type ThreadInitiator, type SpanKind } from '@/services/api/agentApi';
import { ensureOptionalAgentServiceAvailable } from '@/services/api/agentServiceAvailability';

class TraceClient {
  private cachedTraceLevel: number | null = null;
  private initPromise: Promise<void> | null = null;
  private initDone = false;

  /**
   * Initialize the trace client by fetching the effective trace level.
   * Called once on app startup. If it fails, tracing is silently disabled.
   */
  async init(): Promise<void> {
    if (this.initDone) return;
    if (this.initPromise) return this.initPromise;

    this.initPromise = (async () => {
      try {
        if (!(await ensureOptionalAgentServiceAvailable())) {
          this.cachedTraceLevel = 0;
          this.initDone = true;
          return;
        }

        const result = await tracesApi.getEffectiveTraceLevel();
        this.cachedTraceLevel = result.data?.traceLevel ?? 0;
        this.initDone = true;
        if (this.cachedTraceLevel > 0) {
          console.debug(`[TraceClient] Initialized — trace level ${this.cachedTraceLevel}`);
        }
      } catch (err) {
        // Tracing disabled for this session — zero cost
        this.cachedTraceLevel = 0;
        this.initDone = true;
        console.warn('[TraceClient] Failed to initialize — tracing disabled for this session.', err instanceof Error ? err.message : err);
      }
    })();

    return this.initPromise;
  }

  /**
   * Whether tracing is enabled (trace level > 0 and init succeeded).
   */
  isEnabled(): boolean {
    return this.initDone && (this.cachedTraceLevel ?? 0) > 0;
  }

  /**
   * Get the cached trace level.
   */
  getTraceLevel(): number {
    return this.cachedTraceLevel ?? 0;
  }

  /**
   * Create a new trace thread. Returns threadId and traceLevel, or null if disabled.
   */
  async createThread(
    initiator: ThreadInitiator,
    title?: string,
    initiatorDetail?: string
  ): Promise<{ threadId: string; traceLevel: number } | null> {
    if (!this.isEnabled()) return null;

    try {
      const result = await tracesApi.createThread({ initiator, initiatorDetail, title });
      if (result.success && result.data) {
        return {
          threadId: result.data.thread_id,
          traceLevel: result.data.trace_level,
        };
      }
      return null;
    } catch {
      return null; // Fire-and-forget — never break the app
    }
  }

  /**
   * Start a span. Returns spanId or null if disabled/filtered.
   */
  async startSpan(
    threadId: string | null | undefined,
    kind: SpanKind,
    summary: string,
    opts?: {
      parentSpanId?: string;
      detail?: Record<string, unknown>;
      traceLevel?: number;
      agent?: string;
    }
  ): Promise<string | null> {
    if (!this.isEnabled() || !threadId) return null;

    try {
      const result = await tracesApi.createSpan({
        threadId,
        parentSpanId: opts?.parentSpanId,
        turnIndex: 0,
        kind,
        agent: opts?.agent || 'frontend',
        traceLevel: opts?.traceLevel ?? 1,
        summary,
        detail: opts?.detail,
      });
      return result.data?.span_id ?? null;
    } catch {
      return null;
    }
  }

  /**
   * End a span. No-op if spanId is null (filtered span).
   */
  async endSpan(
    spanId: string | null | undefined,
    status: 'ok' | 'error',
    opts?: {
      summary?: string;
      detail?: Record<string, unknown>;
      errorMessage?: string;
    }
  ): Promise<void> {
    if (!spanId) return;

    try {
      await tracesApi.endSpan(spanId, {
        status,
        summary: opts?.summary,
        detail: opts?.detail,
        errorMessage: opts?.errorMessage,
      });
    } catch {
      // Fire-and-forget
    }
  }

  /**
   * Build trace headers for outgoing HTTP requests.
   * Returns empty object if tracing is disabled.
   */
  getTraceHeaders(
    threadId: string | null | undefined,
    parentSpanId?: string | null
  ): Record<string, string> {
    if (!threadId || !this.isEnabled()) return {};
    const headers: Record<string, string> = {
      'X-Trace-Thread-Id': threadId,
      'X-Trace-Level': String(this.cachedTraceLevel ?? 1),
    };
    if (parentSpanId) {
      headers['X-Trace-Parent-Span-Id'] = parentSpanId;
    }
    return headers;
  }
}

export const traceClient = new TraceClient();
